/**
 * GuideMarkdown — renders help-centre article bodies.
 *
 * Ported back from the marketing site's `components/docs/Markdown.tsx` (which was itself a
 * port of this app's `MarkdownBlock.tsx`) when the help centre moved in-app (ClickUp
 * 86d3zt9d3). It stays a SIBLING of `MarkdownBlock` rather than a variant of it: articles
 * need heading ids, lazy screenshot loading, a table scroll-wrap and router links — none of
 * which the chat hot path wants, and `MarkdownBlock` deliberately carries no router
 * dependency.
 *
 * SAFETY — do not relax without a reason written down here:
 *   - Raw HTML is NOT enabled (no `rehype-raw`), so react-markdown escapes embedded HTML
 *     to text.
 *   - There is no `dangerouslySetInnerHTML` anywhere on this path.
 *   - External links get `rel="noopener noreferrer"`.
 *
 * Heading ids come from the shared pure `slugify`, the same function the table of contents
 * uses — see `content/help/slug.ts` for why it carries no de-duplication state.
 *
 * The components map is built once at module scope: react-markdown treats a fresh
 * `components` object as a new renderer on every render (same discipline as
 * `MarkdownBlock`).
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { slugify } from "../../../content/help/slug";
import "./guide-markdown.css";

/** Flatten a heading's children to plain text so it can be slugified. */
function nodeText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join("");
  if (typeof node === "object" && "props" in node) {
    return nodeText((node as { props: { children?: ReactNode } }).props.children);
  }
  return "";
}

const components: Components = {
  a: ({ node: _node, href, children, ...props }) => {
    // In-app links route through React Router so the SPA doesn't do a full reload.
    // In-page anchors deliberately stay native <a>: a router Link would push a history
    // entry and skip the browser's own scroll-to-id behaviour.
    if (href && href.startsWith("/")) {
      return (
        <Link {...props} to={href}>
          {children}
        </Link>
      );
    }
    if (href && href.startsWith("#")) {
      return (
        <a {...props} href={href}>
          {children}
        </a>
      );
    }
    return (
      <a {...props} href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },

  h2: ({ node: _node, children, ...props }) => (
    <h2 {...props} id={slugify(nodeText(children))}>
      {children}
    </h2>
  ),
  h3: ({ node: _node, children, ...props }) => (
    <h3 {...props} id={slugify(nodeText(children))}>
      {children}
    </h3>
  ),

  img: ({ node: _node, ...props }) => (
    // Screenshots are heavy and almost always below the fold.
    <img {...props} loading="lazy" decoding="async" />
  ),

  // Tables can be wider than the column; give them their own scroll container so the
  // page body never scrolls sideways.
  table: ({ node: _node, children, ...props }) => (
    <div className="guide-table-wrap">
      <table {...props}>{children}</table>
    </div>
  ),
};

const GuideMarkdown = ({ text }: { text: string }) => {
  return (
    <div className="mg-guide-md">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
};

export default GuideMarkdown;
